import React, { useState, useCallback } from 'react';
import { useWindowDimensions } from 'react-native';
import { createStackNavigator, type StackNavigationOptions } from '@react-navigation/stack';
import {
  HStack, Box, Image,
} from '@gluestack-ui/themed';
import { NavigationContainerRefWithCurrent } from '@react-navigation/native';

import ModuleEditor from '@/lecturer/ModuleEditor';
import ContentStudio from '@/lecturer/ContentStudio';
import EvaluationDesk from '@/lecturer/EvaluationDesk/EvaluationDesk';
import Engagement from '@/lecturer/EvaluationDesk/Engagement/Engagement';
import EventCalendar from '@/lecturer/EventCalendar/EventCalendar';
import ScheduledChangesScreen from '@/lecturer/ScheduledChanges/ScheduledChangesScreen';
import { LecturerSlotMeetingScreen } from '@/lecturer/EventCalendar/LecturerSlotMeetingScreen';
import { ModulePreviewContainer, usePreviewModal } from '@/common/components/ModulePreview';

import { useTenantSettingsStore } from '@/common/context/TenantSettingsStore';
import AdminAssistant from '@/admin/assistant/AdminAssistant';
import { useUserFeatures } from '@/common/hooks/useUserFeatures';
import useDeepLinkHandler from '../useDeepLinkHandler';
import ResolutionGuard from '../../components/ResolutionGuard';

import LecturerHeaderLeft from './LecturerHeaderLeft';
import LecturerHeaderRight from './LecturerHeaderRight';
import LecturerSidebar from './LecturerSidebar';
import { LecturerRouteParamList } from './types';

const Stack = createStackNavigator<LecturerRouteParamList>();

export type LecturerStackScreenConfig = {
  name: keyof LecturerRouteParamList;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component: React.ComponentType<any>;
  options?: StackNavigationOptions;
};

/** Shared with real-navigator integration tests — keep in sync with the navigator below. */
export const lecturerStackScreens: LecturerStackScreenConfig[] = [
  { name: 'EvaluationDesk', component: EvaluationDesk },
  { name: 'Engagement', component: Engagement },
  { name: 'EventCalendar', component: EventCalendar },
  { name: 'ScheduledChanges', component: ScheduledChangesScreen },
  { name: 'SlotMeetingScreen', component: LecturerSlotMeetingScreen },
  { name: 'ModuleEditor', component: ModuleEditor },
  { name: 'ContentStudio', component: ContentStudio },
];

interface LecturerNavigationProps {
  navigationRef?: NavigationContainerRefWithCurrent<Record<string, object | undefined>>;
}

/**
 * Orchestrator component for the Lecturer role's navigation.
 * Handles the layout, header, sidebar, and stack navigation.
 */
const LecturerNavigation: React.FC<LecturerNavigationProps> = ({ navigationRef }) => {
  const { width, height } = useWindowDimensions();
  const settings = useTenantSettingsStore((state) => state.settings);
  const imageAssetUrls = settings?.imageAssetUrls;
  const { isFeatureActive } = useUserFeatures();
  const { isOpen: isPreviewOpen, closePreview } = usePreviewModal();

  const isAssistantActive = isFeatureActive('lecturer-agent');

  useDeepLinkHandler({ navigationRef });

  // Track current route via state, updated by screen listeners
  const [currentRoute, setCurrentRoute] = useState<string>(() => {
    // Check if the navigation ref is ready and has a current route
    // We handle both direct ref objects and those with a .current property for robustness
    const nav = (navigationRef as unknown as { current: unknown })?.current || navigationRef;
    const navTyped = nav as { isReady?: () => boolean; getCurrentRoute?: () => { name?: string } };
    if (navTyped && typeof navTyped.isReady === 'function' && navTyped.isReady()) {
      const route = navTyped.getCurrentRoute?.();
      if (route?.name) return route.name;
    }
    return 'EvaluationDesk';
  });

  // Screen listener to track route changes
  const screenListeners = useCallback(
    () => ({
      focus: (e: { target?: string }) => {
        // Extract route name from the event target
        const routeName = e.target?.split('-')[0];
        if (routeName) {
          setCurrentRoute(routeName);
        }
      },
    }),
    [],
  );

  const isResolutionMet = width >= 1024 && height >= 500;

  return (
    <Box flex={1} bg="$bgApp" position="relative">
      {!!imageAssetUrls?.logoWatermarkUrl && (
        <Image
          source={{
            uri: imageAssetUrls.logoWatermarkUrl,
            cache: 'force-cache',
          }}
          alt="Watermark"
          position="absolute"
          bottom={0}
          right={0}
          w={250}
          h={500}
          zIndex={0}
          opacity={0.15}
        />
      )}

      {/* Full-width Header Bar */}
      <HStack
        h={56}
        bg="$bgApp"
        borderBottomWidth={1}
        borderBottomColor="$borderLight"
        alignItems="center"
        justifyContent="space-between"
        px="$4"
        softShadow="1"
        zIndex={10}
      >
        <LecturerHeaderLeft />
        <LecturerHeaderRight />
      </HStack>

      {/* Sidebar + Content Area */}
      <HStack flex={1}>
        {currentRoute !== 'ModuleEditor' && currentRoute !== 'ContentStudio' && currentRoute !== 'SlotMeetingScreen' && (
          <LecturerSidebar
            currentRoute={currentRoute}
          />
        )}

        <Box flex={1}>
          <Stack.Navigator
            id="lecturerNavigation"
            initialRouteName="EvaluationDesk"
            screenListeners={screenListeners}
            screenOptions={{
              headerShown: false,
              cardOverlayEnabled: false,
            }}
          >
            {lecturerStackScreens.map(({ name, component, options }) => (
              <Stack.Screen
                key={name}
                name={name}
                component={component}
                options={options}
              />
            ))}
          </Stack.Navigator>
        </Box>
      </HStack>
      {isPreviewOpen && (
        <ModulePreviewContainer
          onClose={closePreview}
        />
      )}
      {!isResolutionMet && (
        <Box position="absolute" top={0} left={0} right={0} bottom={0} zIndex={50}>
          <ResolutionGuard
            width={width}
            height={height}
            headerLeft={<LecturerHeaderLeft />}
            headerRight={<LecturerHeaderRight />}
          />
        </Box>
      )}
      {!!isAssistantActive && currentRoute !== 'SlotMeetingScreen' && <AdminAssistant />}
    </Box>
  );
};

export default LecturerNavigation;
